import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../lib/api';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

export default function MaintenanceNotice() {
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState('');
  const notice = notices[0];

  useEffect(() => {
    setNotices([]);
    setError('');
    if (!user?.id) return;

    let cancelled = false;
    axios.get(`${API}/system-notices/current`)
      .then(({ data }) => {
        if (!cancelled) setNotices(data.notices || []);
      })
      .catch(() => {
        // A temporary API failure must not block the rest of the system.
      });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  useEffect(() => {
    if (!notice) return;
    axios.post(`${API}/system-notices/${encodeURIComponent(notice.id)}/view`, { revision: notice.revision })
      .catch(() => {
        // The acknowledgment request will also register that the notice was viewed.
      });
  }, [notice?.id, notice?.revision]);

  useEffect(() => {
    if (!notice?.expires_at) return;
    const remaining = new Date(notice.expires_at).getTime() - Date.now();
    if (remaining <= 0) {
      setNotices((current) => current.slice(1));
      return;
    }
    const timeout = setTimeout(() => setNotices((current) => current.slice(1)), Math.min(remaining, 2147483647));
    return () => clearTimeout(timeout);
  }, [notice?.id, notice?.expires_at]);

  const acknowledge = async () => {
    if (acknowledging || !notice) return;
    setAcknowledging(true);
    setError('');
    try {
      await axios.post(`${API}/system-notices/${encodeURIComponent(notice.id)}/acknowledge`, { revision: notice.revision });
      setNotices((current) => current.slice(1));
    } catch (requestError) {
      if ([404, 409].includes(requestError.response?.status)) {
        const { data } = await axios.get(`${API}/system-notices/current`).catch(() => ({ data: { notices: [] } }));
        setNotices(data.notices || []);
      } else {
        setError('Não foi possível registrar sua ciência. Tente novamente.');
      }
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <AlertDialog open={Boolean(notice)} onOpenChange={() => {}}>
      <AlertDialogContent
        data-testid="maintenance-notice"
        className="w-[calc(100vw-2rem)] max-w-lg overflow-hidden border-emerald-200 p-0"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="bg-gradient-to-r from-emerald-800 to-teal-700 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            {SHOW_INSTITUTO_BW_BRANDING && (
              <img src="/bw-symbol-final.png" alt="Instituto BW" className="h-14 w-14 rounded-lg bg-white p-1 object-contain" />
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-100">Sistema CRAS</p>
              <p className="text-sm text-emerald-50">Centro de Reabilitação de Animais Silvestres</p>
            </div>
          </div>
        </div>
        <div className="space-y-5 px-6 pb-6 pt-1">
          <AlertDialogHeader className="text-left">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Aviso importante</p>
            <AlertDialogTitle className="text-2xl text-emerald-900">{notice?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-base leading-relaxed text-slate-700">
              {notice?.body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-slate-800">
            <p className="font-semibold">De {notice?.starts_at ? new Date(notice.starts_at).toLocaleString('pt-BR', { timeZone: 'America/Fortaleza' }) : ''}</p>
            <p className="text-sm text-slate-600">Até {notice?.expires_at ? new Date(notice.expires_at).toLocaleString('pt-BR', { timeZone: 'America/Fortaleza' }) : ''} · horário de Fortaleza</p>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <AlertDialogFooter>
            <Button
              type="button"
              data-testid="maintenance-notice-acknowledge"
              className="w-full bg-emerald-700 font-semibold hover:bg-emerald-800 sm:w-auto"
              onClick={acknowledge}
              disabled={acknowledging}
            >
              {acknowledging ? 'Registrando...' : 'CIENTE'}
            </Button>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
